import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Container, Row, Col, Pagination } from "react-bootstrap";
import { BlogCard } from "./BlogCard";
import { useTranslation } from "react-i18next";
import { blogsAPI } from "../../../services/publicPages";
import { getDate } from "../../../utils/dateTime";
import { LoadingDots, ServerDown } from "../../../components/general";
import { DYNAMIC_ROUTES } from "../../../routes/RoutesPathes";
import { ComingSoon } from "../../public/ComingSoon";
import ResourcesPopup from "../../../components/components/js/ResourcesPopup";

const PAGE_SIZE = 6; // 6 = page size from backend
const DISPLAYED_PAGES = 3; // 3 = number of pages displayed in the pagination

export const Resources = ({ blog_type }) => {
  const { t, i18n } = useTranslation();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [empty, setEmpty] = useState(false);
  const [serverUnavailable, setServerUnavailable] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [visiblePagesStart, setVisiblePagesStart] = useState(1);

  // Popup: show every time the page is opened or refreshed
  const [popupOpen, setPopupOpen] = useState(true);

  // fetch blogs for a specific page
  const fetchBlogs = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      const response = await blogsAPI(page, PAGE_SIZE, blog_type); // Filter by blog_type
      setBlogs(response.data.results); // DRF pagination returns `results`
      const count = response.data.count; // total number of blogs
      setTotalPages(Math.ceil(count / PAGE_SIZE));
      if (count === 0) {
        setEmpty(true);
      }
      setLoading(false);
    } catch (err) {
      if (!err?.response) {
        setServerUnavailable(true);
        setLoading(false);
      } else if (err?.response?.status === 404) {
        setLoading(false);
      }
    }
  }, [blog_type]);

  useEffect(() => {
    fetchBlogs(currentPage);
  }, [currentPage, fetchBlogs]);

  // Update visible pages window when currentPage changes
  useEffect(() => {
    // When user moves forward and currentPage exceeds visible window, shift window
    if (currentPage >= visiblePagesStart + DISPLAYED_PAGES) {
      setVisiblePagesStart(currentPage - DISPLAYED_PAGES + 1);
    }
    // When user moves backward before visible window, shift window back
    else if (currentPage < visiblePagesStart) {
      setVisiblePagesStart(currentPage);
    }
  }, [currentPage, visiblePagesStart]);

  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  // Helper: render page numbers for current visible window
  const renderPageNumbers = () => {
    const pages = [];
    for (
      let i = visiblePagesStart;
      i < visiblePagesStart + DISPLAYED_PAGES && i <= totalPages;
      i++
    ) {
      pages.push(
        <Pagination.Item
          key={i}
          active={i === currentPage}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </Pagination.Item>
      );
    }
    return pages;
  };

  // Helper: Show ellipsis before if there's hidden pages beyond page 1
  const renderPrevEllipsis = () => {
    return (
      visiblePagesStart > 1 && (
        <>
          <Pagination.Item onClick={() => handlePageChange(1)}>
            1
          </Pagination.Item>
          {visiblePagesStart > 2 && <Pagination.Ellipsis />}
        </>
      )
    );
  };

  // Helper: Show ellipsis after if there's hidden pages beyond the visible window
  const renderNextEllipsis = () => {
    return (
      visiblePagesStart <= totalPages - DISPLAYED_PAGES && (
        <>
          {visiblePagesStart + DISPLAYED_PAGES < totalPages && (
            <Pagination.Ellipsis />
          )}
          <Pagination.Item onClick={() => handlePageChange(totalPages)}>
            {totalPages}
          </Pagination.Item>
        </>
      )
    );
  };

  return (
    <Container className="my-5">
      <ResourcesPopup open={popupOpen} onClose={() => setPopupOpen(false)} />
      {loading && <LoadingDots />}
      {serverUnavailable && <ServerDown />}
      {empty && <ComingSoon />}
      {!loading && !serverUnavailable && !empty && (
        <>
          <h1 className="mb-4 fw-bold">{t("titles.articles")}</h1>
          <Row>
            {blogs?.map((blog, idx) => (
              <Col key={idx} lg={4} sm={6}>
                <Link
                  key={idx}
                  to={DYNAMIC_ROUTES.blogDetail(blog.slug)}
                  className="text-decoration-none"
                >
                  <BlogCard
                    slug={blog.slug}
                    coverImg={blog.cover_image}
                    title={blog[`title_${i18n.language}`] || blog.title}
                    date={getDate(blog.created_at, i18n.language)}
                  />
                </Link>
              </Col>
            ))}
          </Row>

          <div className="d-flex justify-content-center mt-1">
            <Pagination className="custom-pagination">
              {/* Prev button */}
              <Pagination.Prev
                disabled={currentPage === 1}
                onClick={() => handlePageChange(currentPage - 1)}
              />

              {/* Prev Pagination.Ellipsis */}
              {renderPrevEllipsis()}

              {/* Visible page numbers */}
              {renderPageNumbers()}

              {/* Next Pagination.Ellipsis */}
              {renderNextEllipsis()}

              {/* Next button */}
              <Pagination.Next
                disabled={currentPage === totalPages}
                onClick={() => handlePageChange(currentPage + 1)}
              />
            </Pagination>
          </div>
        </>
      )}
    </Container>
  );
};
